/**
 * SSA Lowering — converts StructuredNode (with SsaInstr[]) to LoweredNode (with Statement[]).
 *
 * This pass:
 * 1. Resolves SSA variables to named Expression trees
 * 2. Inlines single-use SSA values into their use sites
 * 3. Always inlines constants and compares
 * 4. Names multi-use values via the NameProvider
 * 5. Resolves string literal addresses
 */

import { SsaFunction, SsaInstr, SsaValue, SsaVariable, SsaConst, isVariable, isConst, CallTarget } from './SsaTypes';
import { StructuredNode } from './StructuralAnalysis';
import { Expression, Statement, LoweredNode } from './ExpressionIR';
import { lookupKnownFunction } from './KnownFunctions';
import { hoistCommonAssigns, eliminateDeadAssigns, detectForLoops, cleanupLowered, reduceNesting, recoverWhileConditions } from './LoweredTransforms';
import { computeLocalLiveRanges, LiveRange, localBackedIndex } from './LiveRanges';

/** Addresses at or below this threshold are not treated as string pointers. */
const MIN_STRING_ADDRESS = 256;

/** Extracts the WASM local/param index from an SSA variable's base name, or null. */
function localIndexOf(baseName: string): number | null {
  const match = baseName.match(/^(?:local|param)(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/** Derives a result-variable name from the called function's name, or null. */
function callResultName(rawName: string): string | null {
  const known = lookupKnownFunction(rawName);
  if (known && known.returnName) {
    return known.returnName;
  }
  if (rawName.startsWith('is_') || rawName.startsWith('has_') || rawName.startsWith('can_')) {
    return rawName.replace(/^(is_|has_|can_)/, '');
  }
  if (rawName.startsWith('get_')) {
    return rawName.replace(/^get_/, '');
  }
  if (rawName.startsWith('find_') || rawName.startsWith('search_') || rawName.startsWith('lookup_')) {
    return 'found';
  }
  if (rawName.includes('alloc') || rawName.includes('malloc')) {
    return 'buf';
  }
  if (rawName.includes('len') || rawName.includes('length') || rawName.includes('size') || rawName.includes('count')) {
    return 'len';
  }
  return null;
}

/**
 * Names proposed for a function's locals. `byLocalIndex` applies to a whole WASM
 * local (call/flag names); `byVariableId` applies to a single SSA version so that a
 * live range can be named without renaming the slot's other ranges (loop counters).
 */
export interface LocalNameProposals {
  byLocalIndex: Map<number, string>;
  byVariableId: Map<number, string>;
}

/**
 * Names a callee's parameter so a local passed into that argument can borrow it.
 * Returns the parameter's real (name-section / DWARF) name, or null when the callee
 * parameter is itself unnamed.
 */
export type CalleeParameterName = (calleeGlobalIndex: number, parameterIndex: number) => string | null;

/**
 * Proposes names for otherwise-unnamed WASM locals: borrowed from known functions
 * (a local passed as memcpy's first arg becomes "dst"; a local assigned malloc's
 * result becomes "buf"), from the callee's own named parameters (a local passed into
 * a function whose parameter is named "node" becomes "node"), from value shape (a local
 * only ever assigned the constants 0/1 becomes a boolean "flag"), and from loop structure
 * (a counter live range becomes "i"). Only locals whose resolved name is the generated
 * `varN` are eligible; chosen names are de-duplicated against each other and against real
 * (name-section / DWARF) local names. Counter names are keyed per SSA version so a reused
 * slot's counter range is named without touching its other range; the rest key per index.
 */
export function proposeLocalNames(
  ssaFunc: SsaFunction,
  functionName: (globalIndex: number) => string,
  localName: (localIndex: number) => string,
  calleeParameterName: CalleeParameterName,
): LocalNameProposals {
  const byLocalIndex = new Map<number, string>();
  const byVariableId = new Map<number, string>();
  const taken = new Set<string>();
  // Reserve real (name-section / DWARF) local names so a proposal never shadows one.
  for (const variable of ssaFunc.variables) {
    const localIndex = localIndexOf(variable.name);
    if (localIndex !== null && localName(localIndex) !== `var${localIndex}`) {
      taken.add(localName(localIndex));
    }
  }
  proposeNamesFromCalls(ssaFunc, functionName, localName, calleeParameterName, byLocalIndex, taken);
  proposeFlagNames(ssaFunc, localName, byLocalIndex, taken);
  proposeLoopCounterRanges(ssaFunc, localName, byLocalIndex, byVariableId, taken);
  proposeUsageNames(ssaFunc, localName, byLocalIndex, byVariableId, taken);
  return { byLocalIndex, byVariableId };
}

function proposeNamesFromCalls(
  ssaFunc: SsaFunction,
  functionName: (globalIndex: number) => string,
  localName: (localIndex: number) => string,
  calleeParameterName: CalleeParameterName,
  proposals: Map<number, string>,
  taken: Set<string>,
): void {
  const definitionById = new Map<number, SsaInstr>();
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      const result = getResult(instr);
      if (result) {
        definitionById.set(result.id, instr);
      }
    }
  }
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      // Argument names: a local passed as an argument borrows the callee's parameter
      // name — a curated one for a known function, otherwise the callee's own.
      if (instr.kind === 'call' && instr.target >= 0) {
        const calleeName = functionName(instr.target);
        for (let argIndex = 0; argIndex < instr.args.length; argIndex++) {
          const candidate = argumentBorrowName(calleeName, instr.target, argIndex, calleeParameterName);
          if (candidate) {
            proposeLocalName(instr.args[argIndex], candidate, localName, proposals, taken);
          }
        }
      }
      // Return names: a local assigned a known call's result borrows its result name.
      if (instr.kind === 'assign' && isVariable(instr.value)) {
        const valueDef = definitionById.get(instr.value.id);
        if (valueDef && valueDef.kind === 'call' && valueDef.target >= 0) {
          const resultName = callResultName(functionName(valueDef.target));
          if (resultName) {
            proposeLocalName(instr.result, resultName, localName, proposals, taken);
          }
        }
      }
    }
  }
}

// The name an argument should borrow from the callee's parameter: a curated name from
// the known-function table takes precedence (memcpy's first arg is "dst" regardless of
// how the symbols name it), otherwise the callee's own parameter name when it has one.
function argumentBorrowName(
  calleeName: string,
  calleeGlobalIndex: number,
  argIndex: number,
  calleeParameterName: CalleeParameterName,
): string | null {
  const known = lookupKnownFunction(calleeName);
  if (known && argIndex < known.params.length) {
    return known.params[argIndex];
  }
  return calleeParameterName(calleeGlobalIndex, argIndex);
}

// A local whose every set_local writes the constant 0 or 1 (with at least one 1)
// is a boolean flag. A reused/coalesced slot gets a non-0/1 value and disqualifies
// itself, which keeps this from mislabeling pointers or counters.
function proposeFlagNames(
  ssaFunc: SsaFunction,
  localName: (localIndex: number) => string,
  proposals: Map<number, string>,
  taken: Set<string>,
): void {
  for (const localIndex of findFlagLocals(ssaFunc)) {
    if (proposals.has(localIndex) || localName(localIndex) !== `var${localIndex}`) {
      continue;
    }
    const name = nextAvailableName('flag', taken);
    taken.add(name);
    proposals.set(localIndex, name);
  }
}

const LOOP_COUNTER_NAMES = ['i', 'j', 'k', 'n', 'm'];

/**
 * Names loop-counter live ranges i/j/k. A counter range is compared in a condition
 * and stepped by exactly ±1, never stepped by another constant and never used as a
 * direct load/store address (those mark a pointer). Naming the range rather than the
 * whole slot is what makes this safe: a slot the compiler reused as a counter and
 * then a pointer keeps its pointer range as `varN` instead of also becoming `i`.
 */
function proposeLoopCounterRanges(
  ssaFunc: SsaFunction,
  localName: (localIndex: number) => string,
  byLocalIndex: Map<number, string>,
  byVariableId: Map<number, string>,
  taken: Set<string>,
): void {
  const roles = buildVariableRoles(ssaFunc);
  let sequenceIndex = 0;
  for (const range of computeLocalLiveRanges(ssaFunc)) {
    if (range.localIndex < ssaFunc.paramCount || byLocalIndex.has(range.localIndex)) {
      continue;
    }
    if (localName(range.localIndex) !== `var${range.localIndex}` || !isCounterRange(range, roles)) {
      continue;
    }
    const name = nextLoopCounterName(sequenceIndex, taken);
    sequenceIndex++;
    taken.add(name);
    for (const variableId of range.variableIds) {
      byVariableId.set(variableId, name);
    }
  }
}

function isCounterRange(range: LiveRange, roles: Map<number, Set<string>>): boolean {
  let compared = false;
  let steppedByOne = false;
  let disqualified = false;
  for (const variableId of range.variableIds) {
    const role = roles.get(variableId);
    if (!role) {
      continue;
    }
    compared = compared || role.has('compared');
    steppedByOne = steppedByOne || role.has('steppedByOne');
    disqualified = disqualified || role.has('steppedByOther') || role.has('address');
  }
  return compared && steppedByOne && !disqualified;
}

// Names a local live range `ptr` when it is used as a load/store address (a pointer),
// after counters/flags/call-names have claimed their slots. Per-range like the counter
// pass, so a slot the compiler reused as a counter and then a pointer keeps each role's
// own name. Params are left to the call-argument pass; DWARF/name-section names win.
function proposeUsageNames(
  ssaFunc: SsaFunction,
  localName: (localIndex: number) => string,
  byLocalIndex: Map<number, string>,
  byVariableId: Map<number, string>,
  taken: Set<string>,
): void {
  const roles = buildVariableRoles(ssaFunc);
  for (const range of computeLocalLiveRanges(ssaFunc)) {
    if (range.localIndex < ssaFunc.paramCount || byLocalIndex.has(range.localIndex)) {
      continue;
    }
    if (localName(range.localIndex) !== `var${range.localIndex}`) {
      continue;
    }
    if ([...range.variableIds].some(variableId => byVariableId.has(variableId))) {
      continue;
    }
    const name = usageName(range, roles);
    if (name) {
      const unique = nextAvailableName(name, taken);
      taken.add(unique);
      for (const variableId of range.variableIds) {
        byVariableId.set(variableId, unique);
      }
    }
  }
}

function usageName(range: LiveRange, roles: Map<number, Set<string>>): string | null {
  let address = false;
  let loopBound = false;
  let stepped = false;
  for (const variableId of range.variableIds) {
    const role = roles.get(variableId);
    address = address || (role?.has('address') ?? false);
    loopBound = loopBound || (role?.has('loopBound') ?? false);
    stepped = stepped || (role?.has('steppedByOne') ?? false) || (role?.has('steppedByOther') ?? false);
  }
  if (address) {
    return 'ptr';
  }
  // A loop bound is the stable limit; a range that is itself stepped is the moving side.
  if (loopBound && !stepped) {
    return 'count';
  }
  return null;
}

// Classifies how each SSA variable is used, so a live range can be judged a counter.
function buildVariableRoles(ssaFunc: SsaFunction): Map<number, Set<string>> {
  const stepValuesByLocal = collectSelfStepValues(ssaFunc);
  const constById = collectConstants(ssaFunc);
  const defById = buildDefMap(ssaFunc);
  const roles = new Map<number, Set<string>>();
  const addRole = (value: SsaValue, role: string): void => {
    if (!isVariable(value)) {
      return;
    }
    let set = roles.get(value.id);
    if (!set) {
      set = new Set();
      roles.set(value.id, set);
    }
    set.add(role);
  };
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind === 'compare') {
        addRole(instr.left, 'compared');
        addRole(instr.right, 'compared');
      } else if (instr.kind === 'binary' && (instr.op === '+' || instr.op === '-')) {
        markStepRole(instr, constById, stepValuesByLocal, addRole);
      } else if (instr.kind === 'load' || instr.kind === 'store') {
        addRole(instr.address, 'address');
        markAddressBase(instr.address, defById, addRole);
      }
    }
  }
  // Second pass (step roles are now fully marked): the operand a unit-stepped counter is
  // compared against is the loop bound (the `n` in `i < n`) — but only when that operand
  // is loop-stable (never written in terms of itself), so a pointer/offset/accumulator
  // advanced by any amount, constant or variable, isn't mislabelled as the bound.
  const isCounter = (value: SsaValue): boolean => isVariable(value) && (roles.get(value.id)?.has('steppedByOne') ?? false);
  const isSelfModified = (value: SsaValue): boolean => {
    if (!isVariable(value)) {
      return false;
    }
    const localIndex = localBackedIndex(value.name);
    return localIndex !== null && (stepValuesByLocal.get(localIndex)?.size ?? 0) > 0;
  };
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind !== 'compare') {
        continue;
      }
      if (isCounter(instr.left) && !isSelfModified(instr.right)) {
        addRole(instr.right, 'loopBound');
      } else if (isCounter(instr.right) && !isSelfModified(instr.left)) {
        addRole(instr.left, 'loopBound');
      }
    }
  }
  return roles;
}

// Marks the base of an address expression `base + offset` as used-as-address, so a
// pointer walked as base[offset] is disqualified — while a scaled index arr[i]
// (whose address is base + i*stride) leaves the counter `i` untouched, since `i`
// sits inside the multiply, not directly under the address's `+`.
function markAddressBase(
  address: SsaValue,
  defById: Map<number, SsaInstr>,
  addRole: (value: SsaValue, role: string) => void,
): void {
  if (!isVariable(address)) {
    return;
  }
  const def = defById.get(address.id);
  if (def && def.kind === 'binary' && (def.op === '+' || def.op === '-')) {
    addRole(def.left, 'address');
    addRole(def.right, 'address');
  }
}

// Marks a binary `base ± const` as a counter step when its result feeds back into
// base's own local (a genuine self-step like i = i + 1, not a value use like
// a[i - 1]). The constant may be inline or a const-defined variable, and for `+`
// may sit on either side.
function markStepRole(
  instr: SsaInstr & { kind: 'binary' },
  constById: Map<number, number | bigint>,
  stepValuesByLocal: Map<number, Set<number>>,
  addRole: (value: SsaValue, role: string) => void,
): void {
  let base: SsaValue | null = null;
  let magnitude = 0;
  const rightConst = numericConstValue(instr.right, constById);
  if (isVariable(instr.left) && rightConst !== null) {
    base = instr.left;
    magnitude = Math.abs(rightConst);
  } else if (instr.op === '+' && isVariable(instr.right)) {
    const leftConst = numericConstValue(instr.left, constById);
    if (leftConst !== null) {
      base = instr.right;
      magnitude = Math.abs(leftConst);
    }
  }
  if (!base || !isVariable(base)) {
    return;
  }
  const stepLocal = localBackedIndex(base.name);
  if (stepLocal === null || !(stepValuesByLocal.get(stepLocal)?.has(instr.result.id) ?? false)) {
    return;
  }
  addRole(base, magnitude === 1 ? 'steppedByOne' : 'steppedByOther');
}

function collectConstants(ssaFunc: SsaFunction): Map<number, number | bigint> {
  const constById = new Map<number, number | bigint>();
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind === 'const') {
        constById.set(instr.result.id, instr.value);
      }
    }
  }
  return constById;
}

// Numeric value of an inline or const-defined operand, or null if not constant.
function numericConstValue(value: SsaValue, constById: Map<number, number | bigint>): number | null {
  const raw = isConst(value) ? value.value : (isVariable(value) ? constById.get(value.id) : undefined);
  return raw === undefined ? null : Number(raw);
}

// For each local index, the value ids that flow back into it — either through an
// assign (localN_v = value) or as a phi input (the loop back-edge). This lets a
// `localN ± k` expression be recognized as a real step of that local rather than a
// value use like a[i - 1]. The phi case matters when the optimizer drops the
// intermediate assign and wires the increment straight into the loop-header phi.
function collectSelfStepValues(ssaFunc: SsaFunction): Map<number, Set<number>> {
  const stepValuesByLocal = new Map<number, Set<number>>();
  const addStep = (localIndex: number | null, valueId: number): void => {
    if (localIndex === null) {
      return;
    }
    let values = stepValuesByLocal.get(localIndex);
    if (!values) {
      values = new Set();
      stepValuesByLocal.set(localIndex, values);
    }
    values.add(valueId);
  };
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind === 'assign' && isVariable(instr.value)) {
        addStep(localBackedIndex(instr.result.name), instr.value.id);
      } else if (instr.kind === 'phi') {
        const localIndex = localBackedIndex(instr.result.name);
        for (const input of instr.inputs) {
          if (isVariable(input.value)) {
            addStep(localIndex, input.value.id);
          }
        }
      }
    }
  }
  return stepValuesByLocal;
}

function nextLoopCounterName(sequenceIndex: number, taken: Set<string>): string {
  const base = sequenceIndex < LOOP_COUNTER_NAMES.length ? LOOP_COUNTER_NAMES[sequenceIndex] : `i${sequenceIndex + 1}`;
  return nextAvailableName(base, taken);
}

function findFlagLocals(ssaFunc: SsaFunction): number[] {
  const constById = collectConstants(ssaFunc);
  const allFlagConsts = new Map<number, boolean>();
  const assignedOne = new Set<number>();
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind !== 'assign') {
        continue;
      }
      const localIndex = localBackedIndex(instr.result.name);
      if (localIndex === null) {
        continue;
      }
      const value = flagConstValue(instr.value, constById);
      allFlagConsts.set(localIndex, (allFlagConsts.get(localIndex) ?? true) && value !== null);
      if (value === 1) {
        assignedOne.add(localIndex);
      }
    }
  }
  const arithmeticLocals = localsUsedInArithmetic(ssaFunc);
  const flags: number[] = [];
  for (const [localIndex, stillAllFlagConsts] of allFlagConsts) {
    // A genuine boolean flag feeds conditions, compares, and stores — not arithmetic. A constant
    // used as a loop increment or a mask (`i + 1`, `cond & 1`) is assigned only 0/1 too, so exclude
    // any local used as a binary operand to keep those as plain numbers the optimizer can fold.
    if (stillAllFlagConsts && assignedOne.has(localIndex) && !arithmeticLocals.has(localIndex)) {
      flags.push(localIndex);
    }
  }
  return flags.sort((first, second) => first - second);
}

// Locals that appear as an operand of a binary (arithmetic/bitwise/shift) instruction. Such a
// local is being used as a number, not a boolean flag.
function localsUsedInArithmetic(ssaFunc: SsaFunction): Set<number> {
  const used = new Set<number>();
  const note = (value: SsaValue): void => {
    if (isVariable(value)) {
      const localIndex = localBackedIndex(value.name);
      if (localIndex !== null) {
        used.add(localIndex);
      }
    }
  };
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind === 'binary') {
        note(instr.left);
        note(instr.right);
      }
    }
  }
  return used;
}

// 0 or 1 when the value is that constant (inline or via a const definition); else null.
function flagConstValue(value: SsaValue, constById: Map<number, number | bigint>): number | null {
  const numeric = numericConstValue(value, constById);
  return numeric === 0 || numeric === 1 ? numeric : null;
}

function nextAvailableName(base: string, taken: Set<string>): string {
  if (!taken.has(base)) {
    return base;
  }
  let suffix = 2;
  while (taken.has(`${base}${suffix}`)) {
    suffix++;
  }
  return `${base}${suffix}`;
}

function proposeLocalName(
  variable: SsaValue,
  candidateName: string,
  localName: (localIndex: number) => string,
  proposals: Map<number, string>,
  taken: Set<string>,
): void {
  if (!candidateName || !isVariable(variable)) {
    return;
  }
  const localIndex = localIndexOf(variable.name);
  if (localIndex === null || proposals.has(localIndex) || localName(localIndex) !== `var${localIndex}`) {
    return;
  }
  const finalName = nextAvailableName(candidateName, taken);
  taken.add(finalName);
  proposals.set(localIndex, finalName);
}

export interface LoweringNameProvider {
  functionName(globalIndex: number): string;
  localName(localIndex: number): string;
  globalName(globalIndex: number): string;
  resolveAddress?(address: number): string | null;
  /** Per-SSA-version name override (live-range-scoped, e.g. loop counters), or null. */
  variableName?(variableId: number): string | null;
}

export function lowerSsaToStatements(
  node: StructuredNode,
  ssaFunc: SsaFunction,
  names: LoweringNameProvider,
): LoweredNode {
  return new SsaStatementLowering(ssaFunc, names).lower(node);
}

/**
 * Translates a structured SSA function into a LoweredNode tree: resolves SSA
 * variables to names, inlines eligible single-use values into their use sites,
 * and emits statements. The shared maps (defs, uses, inline-eligibility, name
 * collisions) are instance state.
 */
class SsaStatementLowering {
  private readonly defMap: Map<number, SsaInstr>;
  private readonly defCount: Map<number, number>;
  private readonly useCount: Map<number, number>;
  private readonly useSiteContext: Map<number, Set<string>>;
  private readonly unsafeInlines: Set<number>;
  private readonly exprCache = new Map<number, Expression>();
  private readonly resolvedNames = new Map<number, string>();
  private readonly nameUsageCount = new Map<string, number>();
  private readonly reservedLocalNames = new Set<string>();

  constructor(
    private readonly ssaFunc: SsaFunction,
    private readonly names: LoweringNameProvider,
  ) {
    this.defMap = buildDefMap(ssaFunc);
    this.defCount = buildDefCount(ssaFunc);
    this.useCount = buildUseCount(ssaFunc);
    this.useSiteContext = buildUseSiteContext(ssaFunc);
    this.unsafeInlines = buildUnsafeInlines(ssaFunc);
    this.seedReservedLocalNames();
    this.resolveTemporaryNames();
    this.buildExpressionCache();
  }

  // Reserve every effective local/param name so a temporary never resolves to the
  // same identifier as a named local (which would silently shadow it).
  private seedReservedLocalNames(): void {
    for (const variable of this.ssaFunc.variables) {
      const versionName = this.names.variableName?.(variable.id);
      if (versionName) {
        this.reservedLocalNames.add(versionName);
      }
      const localIndex = localBackedIndex(variable.name);
      if (localIndex !== null) {
        this.reservedLocalNames.add(this.names.localName(localIndex));
      }
    }
  }

  lower(node: StructuredNode): LoweredNode {
    return reduceNesting(cleanupLowered(detectForLoops(recoverWhileConditions(hoistCommonAssigns(eliminateDeadAssigns(this.lowerNode(node)))))));
  }

  // A value is inlineable only if it has a single definition (out-of-SSA phi
  // copies can define a variable in several blocks — those must stay as named
  // assignments) and is either single-use or a cheap always-inline expression.
  private isInlineableDefinition(instr: SsaInstr, result: SsaVariable): boolean {
    if ((this.defCount.get(result.id) ?? 0) !== 1) {
      return false;
    }
    const uses = this.useCount.get(result.id) ?? 0;
    // A load reads memory; inlining it past a store/call would reorder the read.
    if (instr.kind === 'load') {
      return uses <= 1 && !this.unsafeInlines.has(result.id) && canInline(instr);
    }
    // A call has side effects: inline only when used exactly once (so it still
    // runs exactly once, at the use site) and not reordered across a store/call.
    if (instr.kind === 'call' || instr.kind === 'call_indirect') {
      return uses === 1 && !this.unsafeInlines.has(result.id) && canInline(instr);
    }
    const alwaysInline = instr.kind === 'const' || instr.kind === 'global_get' || instr.kind === 'compare' || instr.kind === 'unary';
    return (uses <= 1 || alwaysInline) && canInline(instr);
  }

  // Resolve names for non-inlined temporaries, avoiding collisions.
  private resolveTemporaryNames(): void {
    for (const block of this.ssaFunc.blocks) {
      for (const instr of block.instructions) {
        const result = getResult(instr);
        if (!result || this.resolvedNames.has(result.id)) { continue; }
        const baseName = result.name;
        if (baseName.startsWith('local') || baseName.startsWith('param') || baseName.startsWith('phi') || baseName.startsWith('neg')) {
          continue;
        }
        if (this.isInlineableDefinition(instr, result)) {
          continue;
        }
        this.resolvedNames.set(result.id, this.uniqueName(this.computeVarName(result)));
      }
    }
  }

  private uniqueName(candidateName: string): string {
    if (candidateName.startsWith('global_') || candidateName.startsWith('__')) {
      return candidateName;
    }
    const usedCount = this.nameUsageCount.get(candidateName) || 0;
    this.nameUsageCount.set(candidateName, usedCount + 1);
    if (usedCount === 0 && !this.reservedLocalNames.has(candidateName)) {
      return candidateName;
    }
    if (candidateName === 'i') {
      const loopCounterNames = ['i', 'j', 'k', 'n', 'm'];
      for (let index = usedCount; index < loopCounterNames.length; index++) {
        if (!this.reservedLocalNames.has(loopCounterNames[index])) {
          return loopCounterNames[index];
        }
      }
      return `i${usedCount + 1}`;
    }
    let suffix = Math.max(usedCount + 1, 2);
    while (this.reservedLocalNames.has(`${candidateName}${suffix}`)) {
      suffix++;
    }
    return `${candidateName}${suffix}`;
  }

  private buildExpressionCache(): void {
    for (const block of this.ssaFunc.blocks) {
      for (const instr of block.instructions) {
        const result = getResult(instr);
        if (result && this.isInlineableDefinition(instr, result)) {
          const expr = this.instrToExpression(instr);
          if (expr) {
            this.exprCache.set(result.id, expr);
          }
        }
      }
    }
  }

  private constExpression(value: number | bigint, type: string, allowStringLiteral = true): Expression {
    const numericValue = Number(value);
    // A string/address literal is only valid where a pointer is expected. Float
    // constants are never pointers, and arithmetic/condition/address operands are
    // numbers — those callers pass allowStringLiteral=false so a constant that merely
    // happens to point into rodata isn't rendered as e.g. `x * "some string"`.
    if (allowStringLiteral && type !== 'f32' && type !== 'f64' && this.names.resolveAddress && numericValue > MIN_STRING_ADDRESS) {
      const resolved = this.names.resolveAddress(numericValue);
      if (resolved) {
        return { kind: 'string_literal', value: resolved, address: numericValue };
      }
    }
    return { kind: 'const', value, type };
  }

  private resolveValue(value: SsaValue, allowStringLiteral = true): Expression {
    if (isConst(value)) {
      return this.constExpression(value.value, value.type, allowStringLiteral);
    }
    if (isVariable(value)) {
      const def = this.defMap.get(value.id);
      // The expression cache stores a constant already string-resolved; in a context
      // that forbids string literals, rebuild it as a plain const instead.
      if (def && def.kind === 'const' && !allowStringLiteral) {
        return this.constExpression(def.value, def.type, false);
      }
      const cached = this.exprCache.get(value.id);
      if (cached) {
        return cached;
      }
      if (def && def.kind === 'const') {
        return this.constExpression(def.value, def.type, allowStringLiteral);
      }
      return { kind: 'var', name: this.resolveVarName(value), type: value.type };
    }
    return { kind: 'const', value: 0, type: 'i32' };
  }

  private resolveVarName(variable: SsaVariable): string {
    return this.resolvedNames.get(variable.id) ?? this.computeVarName(variable);
  }

  private computeVarName(variable: SsaVariable): string {
    const baseName = variable.name;
    // A live-range-scoped override (e.g. a loop counter) names this specific SSA
    // version, even when its slot's other versions keep the generated name.
    const versionName = this.names.variableName?.(variable.id);
    if (versionName) {
      return versionName;
    }
    const localIndex = localIndexOf(baseName);
    if (localIndex !== null) {
      return this.names.localName(localIndex);
    }
    const phiMatch = baseName.match(/^phi_(\d+)/);
    if (phiMatch) {
      return this.names.localName(parseInt(phiMatch[1], 10));
    }
    // Negation temporaries are always inlined; this is only a diagnostic fallback.
    const negMatch = baseName.match(/^neg_(\d+)/);
    if (negMatch) {
      return `neg_${negMatch[1]}`;
    }
    const def = this.defMap.get(variable.id);
    const fromDefinition = def ? this.nameFromDefinition(def) : null;
    return fromDefinition ?? this.nameFromUseContext(variable);
  }

  private nameFromDefinition(def: SsaInstr): string | null {
    if (def.kind === 'call' && def.target >= 0) {
      return callResultName(this.names.functionName(def.target)) ?? 'result';
    }
    if (def.kind === 'call_indirect') {
      return 'result';
    }
    if (def.kind === 'select') {
      return 'val';
    }
    if (def.kind === 'load') {
      if (def.loadType.includes('load8')) { return 'byte'; }
      if (def.loadType.includes('load16')) { return 'word'; }
      return 'val';
    }
    if (def.kind === 'global_get') {
      const globalName = this.names.globalName(def.globalIndex);
      return globalName === '__stack_pointer' || globalName === 'stack_pointer' ? 'sp' : globalName;
    }
    if (def.kind === 'compare') {
      return 'cond';
    }
    if (def.kind === 'binary' && (def.op === '+' || def.op === '-') && this.isStackPointerArithmetic(def)) {
      return 'fp';
    }
    if (def.kind === 'const' && (Number(def.value) === 0 || Number(def.value) === 1)) {
      return 'flag';
    }
    return null;
  }

  private isStackPointerArithmetic(def: SsaInstr & { kind: 'binary' }): boolean {
    if (!isVariable(def.left)) {
      return false;
    }
    const leftDef = this.defMap.get(def.left.id);
    if (!leftDef || leftDef.kind !== 'global_get') {
      return false;
    }
    const globalName = this.names.globalName(leftDef.globalIndex);
    return globalName === '__stack_pointer' || globalName === 'stack_pointer';
  }

  private nameFromUseContext(variable: SsaVariable): string {
    const contexts = this.useSiteContext.get(variable.id);
    if (contexts) {
      // Loop counter: compared in a condition and incremented each iteration.
      if (contexts.has('compared') && contexts.has('incremented')) { return 'i'; }
      if (contexts.has('address')) { return 'ptr'; }       // address operand of a load/store
      if (contexts.has('shifted')) { return 'idx'; }       // shifted → array index computation
    }
    return 'val';
  }

  private resolveCallTarget(instr: SsaInstr & { kind: 'call' }): string {
    if (instr.target >= 0) {
      return this.names.functionName(instr.target);
    }
    if (instr.target === CallTarget.MemorySize) { return 'memory_size'; }
    if (instr.target === CallTarget.MemoryGrow) { return 'memory_grow'; }
    if (instr.target === CallTarget.AtomicCompareExchange) { return 'atomic_cmpxchg'; }
    if (instr.target === CallTarget.ResultIntrinsic && instr.result) {
      return this.ssaFunc.intrinsicNames.get(instr.result.id) || 'intrinsic';
    }
    // Void intrinsic — target encodes the intrinsic name ID.
    return this.ssaFunc.intrinsicNames.get(instr.target) || 'intrinsic';
  }

  private instrToExpression(instr: SsaInstr): Expression | null {
    switch (instr.kind) {
      case 'const':
        return this.constExpression(instr.value, instr.type);
      case 'binary':
        return { kind: 'binary', op: instr.op, left: this.resolveValue(instr.left, false), right: this.resolveValue(instr.right, false) };
      case 'unary':
        return { kind: 'unary', op: instr.op, operand: this.resolveValue(instr.operand, false) };
      case 'compare':
        return { kind: 'compare', op: instr.op, left: this.resolveValue(instr.left, false), right: this.resolveValue(instr.right, false) };
      case 'load':
        return { kind: 'load', address: this.resolveValue(instr.address, false), offset: instr.offset, loadType: instr.loadType };
      case 'convert':
        return { kind: 'convert', op: instr.op, operand: this.resolveValue(instr.operand, false) };
      case 'select':
        return { kind: 'select', condition: this.resolveValue(instr.condition, false), trueVal: this.resolveValue(instr.trueVal), falseVal: this.resolveValue(instr.falseVal) };
      case 'global_get':
        return { kind: 'global', name: this.names.globalName(instr.globalIndex) };
      case 'assign':
        return this.resolveValue(instr.value);
      case 'call':
        return instr.result ? { kind: 'call', name: this.resolveCallTarget(instr), args: instr.args.map(arg => this.resolveValue(arg)) } : null;
      case 'call_indirect':
        return instr.result ? { kind: 'call_indirect', tableIndex: this.resolveValue(instr.tableIndex, false), args: instr.args.map(arg => this.resolveValue(arg)) } : null;
      default:
        return null;
    }
  }

  private instrToStatement(instr: SsaInstr): Statement | null {
    const result = getResult(instr);
    // Inlined values appear at their use site, not as a statement here.
    if (result && this.exprCache.has(result.id)) {
      return null;
    }
    switch (instr.kind) {
      case 'const':
        return { kind: 'assign', target: this.resolveVarName(instr.result), type: instr.type, value: { kind: 'const', value: instr.value, type: instr.type } };
      case 'store':
        return { kind: 'store', address: this.resolveValue(instr.address, false), offset: instr.offset, storeType: instr.storeType, value: this.resolveValue(instr.value) };
      case 'call':
        return { kind: 'call', name: this.resolveCallTarget(instr), args: instr.args.map(arg => this.resolveValue(arg)), result: instr.result ? this.resolveVarName(instr.result) : null };
      case 'call_indirect':
        return { kind: 'call_indirect', tableIndex: this.resolveValue(instr.tableIndex, false), args: instr.args.map(arg => this.resolveValue(arg)), result: instr.result ? this.resolveVarName(instr.result) : null };
      case 'global_set':
        return { kind: 'global_set', name: this.names.globalName(instr.globalIndex), value: this.resolveValue(instr.value) };
      case 'return':
        return { kind: 'return', values: instr.values.map(value => this.resolveValue(value)) };
      case 'unreachable':
        return { kind: 'unreachable' };
      case 'phi':
      case 'branch':
        return null;
      case 'branch_if':
      case 'branch_table':
        // A conditional terminator inside a block body means structuring failed for
        // this region. Dropping it would silently fall through and lose a branch, so
        // fail the function rather than emit wrong control flow.
        throw new Error(`unstructured ${instr.kind} reached lowering`);
      default: {
        if (!result) {
          return null;
        }
        const expr = this.instrToExpression(instr);
        if (!expr) {
          return null;
        }
        return { kind: 'assign', target: this.resolveVarName(result), type: instr.kind === 'compare' ? 'i32' : result.type, value: expr };
      }
    }
  }

  private lowerBlock(node: StructuredNode & { kind: 'block' }): LoweredNode {
    const statements: Statement[] = [];
    for (const instr of node.body) {
      const statement = this.instrToStatement(instr);
      if (statement) {
        statements.push(statement);
      }
    }
    return { kind: 'block', body: statements };
  }

  private lowerNode(node: StructuredNode): LoweredNode {
    switch (node.kind) {
      case 'sequence':
        return { kind: 'sequence', children: node.children.map(child => this.lowerNode(child)) };
      case 'block':
        return this.lowerBlock(node);
      case 'if':
        return { kind: 'if', condition: this.resolveValue(node.condition, false), thenBody: this.lowerNode(node.thenBody), elseBody: node.elseBody ? this.lowerNode(node.elseBody) : null };
      case 'while':
        return { kind: 'while', condition: node.condition ? this.resolveValue(node.condition, false) : null, body: this.lowerNode(node.body) };
      case 'do_while':
        return { kind: 'do_while', body: this.lowerNode(node.body), condition: this.resolveValue(node.condition, false) };
      case 'switch':
        return { kind: 'switch', selector: this.resolveValue(node.selector, false), cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: this.lowerNode(caseEntry.body) })), defaultBody: this.lowerNode(node.defaultBody) };
      case 'break':
        return { kind: 'break' };
      case 'continue':
        return { kind: 'continue' };
      case 'return':
        return { kind: 'return', values: node.value ? [this.resolveValue(node.value)] : [] };
      case 'unreachable':
        return { kind: 'unreachable' };
      case 'labeled_block':
        return { kind: 'labeled_block', label: node.label, body: this.lowerNode(node.body) };
      case 'labeled_break':
        return { kind: 'labeled_break', label: node.label };
      case 'labeled_continue':
        return { kind: 'labeled_continue', label: node.label };
      case 'label':
        return { kind: 'label', name: node.name };
      case 'goto':
        return { kind: 'goto', target: node.target };
    }
  }
}

// ─── Helpers ───

function getResult(instr: SsaInstr): SsaVariable | null {
  switch (instr.kind) {
    case 'phi': case 'assign': case 'const': case 'binary': case 'unary':
    case 'compare': case 'load': case 'convert': case 'select': case 'global_get':
      return instr.result;
    case 'call': case 'call_indirect':
      return instr.result || null;
    default:
      return null;
  }
}

function canInline(instr: SsaInstr): boolean {
  if (instr.kind === 'assign') {
    if (instr.result.name.startsWith('local') || instr.result.name.startsWith('phi')) {
      return false;
    }
    return true;
  }
  // Calls have side effects, so isInlineableDefinition only allows them when the
  // result is used exactly once and not reordered past another store/call.
  return instr.kind === 'const' || instr.kind === 'binary' || instr.kind === 'unary' ||
    instr.kind === 'compare' || instr.kind === 'convert' || instr.kind === 'global_get' ||
    instr.kind === 'load' || instr.kind === 'select' ||
    instr.kind === 'call' || instr.kind === 'call_indirect';
}

function buildDefMap(ssaFunc: SsaFunction): Map<number, SsaInstr> {
  const defMap = new Map<number, SsaInstr>();
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      const result = getResult(instr);
      if (result) {
        defMap.set(result.id, instr);
      }
    }
  }
  return defMap;
}

/** Counts how many instructions define each variable. >1 after out-of-SSA phi copies. */
function buildDefCount(ssaFunc: SsaFunction): Map<number, number> {
  const defCount = new Map<number, number>();
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      const result = getResult(instr);
      if (result) {
        defCount.set(result.id, (defCount.get(result.id) ?? 0) + 1);
      }
    }
  }
  return defCount;
}

interface UseSite {
  blockId: number;
  index: number;
}

function buildUseSites(ssaFunc: SsaFunction): Map<number, UseSite[]> {
  const useSites = new Map<number, UseSite[]>();
  for (const block of ssaFunc.blocks) {
    for (let index = 0; index < block.instructions.length; index++) {
      visitOperands(block.instructions[index], (value) => {
        if (isVariable(value)) {
          const sites = useSites.get(value.id) ?? [];
          sites.push({ blockId: block.id, index });
          useSites.set(value.id, sites);
        }
      });
    }
  }
  return useSites;
}

function writesMemory(instr: SsaInstr): boolean {
  return instr.kind === 'store' || instr.kind === 'call' || instr.kind === 'call_indirect';
}

/**
 * Identifies load and call results that must NOT be inlined because inlining
 * would move the memory read / call past a store or another call. A single-use
 * result is safe to inline only when its use is in the same block with no memory
 * writer between the definition and the use.
 */
function buildUnsafeInlines(ssaFunc: SsaFunction): Set<number> {
  const useSites = buildUseSites(ssaFunc);
  const unsafe = new Set<number>();
  for (const block of ssaFunc.blocks) {
    for (let defIndex = 0; defIndex < block.instructions.length; defIndex++) {
      const instr = block.instructions[defIndex];
      if (instr.kind !== 'load' && instr.kind !== 'call' && instr.kind !== 'call_indirect') {
        continue;
      }
      const result = getResult(instr);
      if (!result) {
        continue;
      }
      const sites = useSites.get(result.id) ?? [];
      if (sites.length !== 1) {
        continue;
      }
      const useSite = sites[0];
      if (useSite.blockId !== block.id) {
        unsafe.add(result.id);
        continue;
      }
      for (let between = defIndex + 1; between < useSite.index; between++) {
        if (writesMemory(block.instructions[between])) {
          unsafe.add(result.id);
          break;
        }
      }
    }
  }
  return unsafe;
}

/**
 * Counts non-phi uses of each SSA variable for inlining decisions.
 * Phi inputs are excluded because they represent the same value flowing through
 * different control paths — a variable used once in real code and once in a phi
 * is effectively single-use and safe to inline.
 */
function buildUseCount(ssaFunc: SsaFunction): Map<number, number> {
  const useCount = new Map<number, number>();
  function count(value: SsaValue): void {
    if (isVariable(value)) {
      useCount.set(value.id, (useCount.get(value.id) || 0) + 1);
    }
  }
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind === 'phi') { continue; }
      visitOperands(instr, count);
    }
  }
  return useCount;
}

function buildUseSiteContext(ssaFunc: SsaFunction): Map<number, Set<string>> {
  const contextMap = new Map<number, Set<string>>();
  function addContext(value: SsaValue, context: string): void {
    if (isVariable(value)) {
      let contexts = contextMap.get(value.id);
      if (!contexts) {
        contexts = new Set();
        contextMap.set(value.id, contexts);
      }
      contexts.add(context);
    }
  }
  for (const block of ssaFunc.blocks) {
    for (const instr of block.instructions) {
      if (instr.kind === 'load') {
        addContext(instr.address, 'address');
      } else if (instr.kind === 'store') {
        addContext(instr.address, 'address');
      } else if (instr.kind === 'compare') {
        addContext(instr.left, 'compared');
        addContext(instr.right, 'compared');
      } else if (instr.kind === 'binary') {
        if (instr.op === 'shl' || instr.op === 'shr_u' || instr.op === 'shr_s') {
          addContext(instr.left, 'shifted');
        }
      }
    }
    // Detect increment patterns: phi variable that gets incremented → loop counter
    for (const instr of block.instructions) {
      if (instr.kind === 'binary' && (instr.op === '+' || instr.op === '-')) {
        if (isVariable(instr.left) && !isVariable(instr.right)) {
          const constVal = Number((instr.right as SsaConst).value);
          if (constVal === 1 || constVal === -1 || constVal === 2 || constVal === 4 || constVal === 8) {
            addContext(instr.left, 'incremented');
          }
        }
      }
    }
  }
  return contextMap;
}

function visitOperands(instr: SsaInstr, visitor: (v: SsaValue) => void): void {
  switch (instr.kind) {
    case 'phi': for (const input of instr.inputs) { visitor(input.value); } break;
    case 'assign': visitor(instr.value); break;
    case 'binary': visitor(instr.left); visitor(instr.right); break;
    case 'unary': visitor(instr.operand); break;
    case 'compare': visitor(instr.left); visitor(instr.right); break;
    case 'load': visitor(instr.address); break;
    case 'store': visitor(instr.address); visitor(instr.value); break;
    case 'call': for (const arg of instr.args) { visitor(arg); } break;
    case 'call_indirect': visitor(instr.tableIndex); for (const arg of instr.args) { visitor(arg); } break;
    case 'convert': visitor(instr.operand); break;
    case 'select': visitor(instr.condition); visitor(instr.trueVal); visitor(instr.falseVal); break;
    case 'global_set': visitor(instr.value); break;
    case 'branch_if': visitor(instr.condition); break;
    case 'branch_table': visitor(instr.selector); break;
    case 'return': for (const retVal of instr.values) { visitor(retVal); } break;
  }
}
